import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, KeyRound } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { AppHeader } from '@/components/AppHeader'
import { Button } from '@/components/Button'
import { useI18n } from '@/i18n/i18n'
import { ApiError, api } from '@/lib/api'
import { isComposing } from '@/lib/ime'
import { formatUsd } from '@/session/describe'
import { ConnectedApps } from './ConnectedApps'

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-line bg-surface p-5 shadow-sm">
      <h2 className="text-[15px] font-semibold">{title}</h2>
      <div className="mt-2">{children}</div>
    </section>
  )
}

export function Settings() {
  const { t } = useI18n()
  const qc = useQueryClient()
  const status = useQuery({ queryKey: ['ai-status'], queryFn: api.ai.status })
  const [key, setKey] = useState('')
  const [editing, setEditing] = useState(false)
  const save = useMutation({
    mutationFn: () => api.ai.saveKey(key.trim()),
    onSuccess: () => {
      setKey('')
      setEditing(false)
      toast(t('settings.keySaved'))
      void qc.invalidateQueries({ queryKey: ['ai-status'] })
    },
    onError: (e) => {
      const code = e instanceof ApiError ? e.code : ''
      toast.error(
        code === 'invalid_key_format'
          ? t('settings.keyInvalid')
          : code === 'key_rejected'
            ? t('settings.keyRejected')
            : code === 'byok_unavailable'
              ? t('settings.keyUnavailable')
              : t('error.generic'),
      )
    },
  })
  const remove = useMutation({
    mutationFn: api.ai.deleteKey,
    onSuccess: () => {
      toast(t('settings.keyDeleted'))
      void qc.invalidateQueries({ queryKey: ['ai-status'] })
    },
  })
  const s = status.data
  const left = s ? Math.max(0, s.credit.grantedMicros - s.credit.spentMicros) : 0
  const showForm = s && (!s.key || editing)

  return (
    <div className="min-h-full">
      <AppHeader>
        <Link
          to="/"
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[13px] text-muted hover:bg-surface-2 hover:text-ink"
        >
          <ChevronLeft size={15} />
          {t('settings.back')}
        </Link>
      </AppHeader>
      <main className="mx-auto w-full max-w-2xl space-y-4 px-4 pt-10 pb-24 sm:px-6">
        <h1 className="mb-6 text-[26px] font-semibold tracking-[-0.025em]">
          {t('settings.title')}
        </h1>
        {!s && <div className="h-40 animate-pulse rounded-lg bg-surface-2" />}
        {s && (
          <>
            <Card title={t('settings.credit')}>
              <div className="h-2 overflow-hidden rounded-full bg-surface-2">
                <div
                  className="h-full rounded-full bg-ai transition-[width]"
                  style={{ width: `${(left / Math.max(1, s.credit.grantedMicros)) * 100}%` }}
                />
              </div>
              <p className="mt-2 text-[13px] text-muted" data-testid="credit-left">
                {s.key
                  ? t('settings.usingKey')
                  : t('settings.creditBody', {
                      left: formatUsd(left),
                      total: formatUsd(s.credit.grantedMicros),
                    })}
              </p>
            </Card>

            <Card title={t('settings.key')}>
              <p className="text-[13px] leading-relaxed text-muted">{t('settings.keyBody')}</p>
              {s.key && !editing && (
                <div className="mt-3 flex items-center gap-3">
                  <span
                    className="inline-flex items-center gap-2 rounded-md border border-line bg-bg px-3 py-1.5 font-mono text-[13px]"
                    data-testid="key-last4"
                  >
                    <KeyRound size={14} className="text-muted" />
                    {t('settings.keyCurrent', { last4: s.key.last4 })}
                  </span>
                  <span className="flex-1" />
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditing(true)
                    }}
                  >
                    {t('settings.keyReplace')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-danger hover:text-danger"
                    onClick={() => {
                      remove.mutate()
                    }}
                  >
                    {t('settings.keyDelete')}
                  </Button>
                </div>
              )}
              {showForm && (
                <form
                  className="mt-3 flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (key.trim()) save.mutate()
                  }}
                >
                  <label className="sr-only" htmlFor="api-key">
                    {t('settings.key')}
                  </label>
                  <input
                    id="api-key"
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    value={key}
                    placeholder={t('settings.keyPlaceholder')}
                    onChange={(e) => {
                      setKey(e.target.value)
                    }}
                    onKeyDown={(e) => {
                      if (isComposing(e)) e.stopPropagation()
                    }}
                    className="h-9 min-w-0 flex-1 rounded-md border border-line bg-bg px-3 font-mono text-[13px] outline-none focus:border-(--focus) focus:shadow-[0_0_0_3px_var(--ai-soft)]"
                  />
                  <Button type="submit" variant="primary" disabled={!key.trim() || save.isPending}>
                    {t('settings.keySave')}
                  </Button>
                </form>
              )}
            </Card>

            <Card title={t('settings.usage')}>
              {s.month.boards.length === 0 ? (
                <p className="text-[13px] text-muted">{t('settings.usageEmpty')}</p>
              ) : (
                <>
                  <p className="text-[13px] text-muted">
                    {t('settings.usageTotal', { total: formatUsd(s.month.totalMicros) })}
                  </p>
                  <ul className="mt-3 divide-y divide-line">
                    {s.month.boards.map((b) => (
                      <li
                        key={b.boardId}
                        className="flex items-center justify-between gap-4 py-2 text-[13px]"
                      >
                        <Link to={`/b/${b.boardId}`} className="truncate hover:underline">
                          {b.title || t('boards.untitled')}
                        </Link>
                        <span className="tabular-nums text-muted">{formatUsd(b.micros)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Card>
          </>
        )}
        <ConnectedApps />
      </main>
    </div>
  )
}
